<template>
  <div
    v-if="question"
    :id="`question_field_${question.id}`"
    class="flex flex-col w-full gap-2 px-4 py-5 transition-all duration-300 border-l-8 form-question lg:w-148 lg:px-10 bg-pure-white rounded-xl"
    :class="[answerInput ? 'border-lime-500' : 'border-slate-300']"
  >
    <BlTextSubtitle2 :text="question.label" />
    <input
      v-if="question.input_type === 'text'"
      type="text"
      :name="`answer_input_${question.id}`"
      :id="`answer_input_${question.id}`"
      class="text-input focus:border-light-purple"
      placeholder="Type here"
      v-model="answerInput"
    />
    <textarea
      v-if="question.input_type === 'textarea'"
      type="text"
      :name="`answer_input_${question.id}`"
      :id="`answer_input_${question.id}`"
      class="textarea-input focus:border-light-purple"
      placeholder="Type here"
      v-model="answerInput"
    ></textarea>
    <input
      v-if="question.input_type === 'date'"
      type="date"
      :name="`answer_input_${question.id}`"
      :id="`answer_input_${question.id}`"
      class="text-input focus:border-light-purple"
      placeholder="Type here"
      v-model="answerInput"
    />
    <div
      v-if="
        question.input_type === 'radio' || question.input_type === 'checkbox'
      "
      class="flex flex-col w-full gap-2"
    >
      <div
        v-for="(opt, idx) in question.input_options"
        :key="idx"
        class="flex items-center gap-2 cursor-pointer"
        :title="opt"
        @click="onSelectOption(opt)"
      >
        <BlInputRadio
          v-if="question.input_type === 'radio'"
          :name="`answer_input_${question.id}`"
          :checked="opt === answerInput"
          @changed="onSelectOption(opt)"
        />
        <BlInputCheck
          v-if="question.input_type === 'checkbox'"
          :name="`answer_input_${question.id}`"
          :checked="answerInput.includes(opt)"
          @changed="onSelectOption(opt)"
        />
        <BlTextBody2 :text="opt" class="truncate text-slate-800" />
      </div>

      <div v-if="question.is_use_other" class="flex items-center gap-2">
        <div
          class="flex items-center gap-2 cursor-pointer"
          @click="isOther = !isOther"
        >
          <BlInputRadio
            v-if="question.input_type === 'radio'"
            :name="`answer_input_${question.id}`"
            :checked="isOther"
            @changed="isOther = !isOther"
          />
          <BlInputCheck
            v-if="question.input_type === 'checkbox'"
            :name="`answer_input_${question.id}`"
            :checked="isOther"
            @changed="isOther = !isOther"
          />
          <BlTextBody2 text="Other:" class="text-slate-800" />
        </div>
        <input
          v-if="!isOther"
          type="text"
          :name="`answer_input_${question.id}`"
          :id="`answer_input_${question.id}`"
          class="opacity-50 pointer-events-none text-input bg-slate-200"
          disabled
          placeholder="Type here"
        />
        <input
          v-else
          type="text"
          :name="`answer_input_${question.id}`"
          :id="`answer_input_${question.id}`"
          class="text-input focus:border-light-purple"
          :class="{ 'bg-slate-400 opacity-50 pointer-events-none': !isOther }"
          :disabled="!isOther"
          :placeholder="
            question.input_type === 'radio'
              ? 'Type here'
              : `${question.input_options.join(', ')}, etc.`
          "
          v-model="answerInput"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import BlIcon from "../../BlIcon.vue";
import BlTextSubtitle2 from "../../BlTextSubtitle2.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlSelectBox from "../../BlSelectBox.vue";
import BlInputRadio from "../../BlInputRadio.vue";
import BlInputCheck from "../../BlInputCheck.vue";
import BlInputButton from "../../BlInputButton.vue";

export default {
  name: "ProspectFormQuestion",
  components: {
    BlIcon,
    BlTextSubtitle2,
    BlTextBody2,
    BlSelectBox,
    BlInputRadio,
    BlInputCheck,
    BlInputButton
  },
  props: {
    sectionId: { type: Number, required: true },
    question: { type: Object, required: true }
  },
  data() {
    return {
      field: { answer: "" },
      isOther: false,
      answerInput: ""
    };
  },
  computed: {
    ...mapGetters(["prospectFormAnswers"])
  },
  watch: {
    prospectFormAnswers: {
      deep: true,
      handler(val) {
        this.generateAnswer(val);
      }
    },
    isOther(val) {
      if (val && this.question.input_type === "radio") {
        this.answerInput = "";
      }
    },
    answerInput(val) {
      if (val !== this.field.answer) {
        this.onEditing(val);
      }
    }
  },
  mounted() {
    this.generateAnswer(this.prospectFormAnswers);
  },
  methods: {
    ...mapActions(["updateProspectAnswer"]),
    generateAnswer(arr) {
      const found = arr.find(i => i.question_id === this.question.id);
      const defaultvalue = this.question.default_value || "";
      if (!found) {
        this.field = { answer: defaultvalue };
        this.answerInput = defaultvalue;
      } else {
        this.field = found;
        this.answerInput = found.answer || defaultvalue;
      }
    },
    onSelectOption(opt) {
      if (this.question.input_type === "radio") {
        this.isOther = false;
        this.answerInput = opt;
      }
      if (this.question.input_type === "checkbox") {
        let arr = this.answerInput.split(",") || [];
        if (arr.includes(opt)) {
          arr = arr.filter(i => i !== opt);
        } else {
          arr = [...arr, opt];
        }
        this.answerInput = arr.filter(i => i).join(",");
      }
    },
    onEditing(val) {
      const data = {
        section_id: this.sectionId,
        question_id: this.question.id,
        answer: val
      };
      this.updateProspectAnswer(data);
    }
  }
};
</script>

<style scoped lang="scss">
.form-question {
  .text-input {
    width: 100%;
    height: 1.875rem;
    padding: 4px 8px 4px 8px;
    border-radius: 5px;
    border: 1px solid var(--Slate-5, #d0d5dd);
    //
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.375rem;
    letter-spacing: 0.5px;
    text-align: left;
    color: var(--Slate-8, #475467);
    //
    outline: none;
  }
  .textarea-input {
    width: 100%;
    height: 6.25rem;
    padding: 4px 8px 4px 8px;
    border-radius: 5px;
    border: 1px solid var(--Slate-5, #d0d5dd);
    //
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.375rem;
    letter-spacing: 0.5px;
    text-align: left;
    color: var(--Slate-8, #475467);
    //
    outline: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/form/prospectForm/Question.vue